import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
    FaSpinner, FaArrowLeft, FaEye, FaFilter, FaTimes, FaBarcode, FaMoneyBillWave,
    // Novos ícones para pesquisa e seleção
    FaSearch, FaUsers, FaUser, FaTrash, FaEdit
} from 'react-icons/fa';

import { ui } from '../utils/styles';
import DividaModal from './DividaModal';

// Função auxiliar para formatar a data como "YYYY-MM-DD" (necessário para o input type="date")
const formatarDataInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Função para calcular o primeiro e o último dia do mês corrente
const getDatasCorrentes = () => {
    const today = new Date();
    const primeiroDia = new Date(today.getFullYear(), today.getMonth(), 1);
    const ultimoDia = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
        inicio: formatarDataInput(primeiroDia),
        fim: formatarDataInput(ultimoDia),
    };
};

const GlobalDividasScreen = () => {
    const navigate = useNavigate();
    const { inicio, fim } = getDatasCorrentes();

    const [allDividas, setAllDividas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedClienteContratanteId, setSelectedClienteContratanteId] = useState(0);

    // --- NOVOS ESTADOS DE FILTRO GERAL ---
    // const [nomeFilter, setNomeFilter] = useState('');
    // const [cpfCnpjFilter, setCpfCnpjFilter] = useState('');

    const [filterDataInicio, setFilterDataInicio] = useState(inicio);
    const [filterDataFim, setFilterDataFim] = useState(fim);
    const [filterStatus, setFilterStatus] = useState('Aberto');
    const [allDevedores, setAllDevedores] = useState([]); // Novo estado
    const [clientesContratantes, setClientesContratantes] = useState([]); // Era emitentes


    // Filtros de Texto/ID: Usaremos IDs ou termos de pesquisa
    const [filterDevedorId, setFilterDevedorId] = useState(0);
    const [filterClienteContratanteId, setFilterClienteContratanteId] = useState(0);

    // ... estados de modais e URL_BACKEND (o modal não será usado nessa versão)
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedDivida, setSelectedDivida] = useState(null);

    // ...
    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );
    // Emitente
    const emitente = useMemo(
        () => localStorage.getItem('userIdEmitente'),
        []
    );

    const fetchDevedores = async () => {
        setLoading(true);
        try {
            // NOVO ENDPOINT DEVEDORES
            // O endpoint deve aceitar Status, ID do Cliente Contratante, Nome e CPF/CNPJ
            const { data } = await axios.get(
                `${URL_BACKEND}/devedor`,
                { headers: authHeader }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllDevedores(arr);
            setCurrentPage(1);
            // toast.success(`Devedores carregados com sucesso! (${arr.length} registros)`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar devedores.');
            setAllDevedores([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchClientesContratantes = async () => {
        setLoading(true);
        try {
            // Reutilizando o endpoint de emitentes/clientes para popular o filtro
            const { data } = await axios.get(
                `${URL_BACKEND}/clientes/Todos/${emitente}`,
                { headers: authHeader }
            );
            setClientesContratantes(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error('Erro ao carregar a lista de Clientes Contratantes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setFilterDataInicio('1900-01-01');
        setFilterDataFim('2500-12-31');
        fetchDevedores();
        fetchClientesContratantes();
        fetchDividas();
    }, []);


    // Função de conversão BRL para Number (necessária para somar os totais)
    const convertBRLToNumber = (brlString) => {
        if (!brlString) return 0;
        let str = brlString.toString();
        str = str.replace(/,/g, '.');
        return Number(str) || 0;
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'R$ 0,00';
        return `R$ ${Number(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    };

    // --- CÁLCULO TOTAL (Soma de todos os valores originais/saldo, dependendo da sua regra) ---
    const totalDividasAbertas = useMemo(() => {
        return allDividas.reduce((acc, divida) => {
            if (divida.status === 'Aberto') {
                // Use valor_original ou valor_saldo dependendo da regra, e converta
                return acc + convertBRLToNumber(divida.valor_original);
            }
            return acc;
        }, 0);
    }, [allDividas]);


    // --- NOVA FUNÇÃO DE API (fetchDividas) ---
    const fetchDividas = async () => {
        setLoading(true);

        const params = new URLSearchParams();

        // 1. FILTROS DE DATA
        if (filterDataInicio) params.append('dataVencimentoDe', filterDataInicio);
        if (filterDataFim) params.append('dataVencimentoAte', filterDataFim);

        // 2. FILTRO DE STATUS
        params.append('status', filterStatus);

        // 3. FILTROS DE PESQUISA (NOVOS)
        if (filterDevedorId) params.append('devedorId', filterDevedorId);
        if (filterClienteContratanteId) params.append('clienteContratanteId', filterClienteContratanteId);

        const queryString = params.toString();

        try {
            // Chamada ao NOVO ENDPOINT (geral, sem ID de devedor)
            const { data } = await axios.get(
                `${URL_BACKEND}/dividas/todas${queryString ? `?${queryString}` : ''}`,
                { headers: authHeader }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllDividas(arr);
            // toast.success(`Total de Dívidas encontradas: ${arr.length}`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar dívidas globais.');
            setAllDividas([]);
        } finally {
            setLoading(false);
        }
    };

    // Função para limpar todos os filtros
    const handleClearFilters = () => {
        const { inicio, fim } = getDatasCorrentes();
        setFilterDataInicio(inicio);
        setFilterDataFim(fim);
        setFilterStatus('Todas');
        setFilterDevedorId(0);
        setFilterClienteContratanteId(0);
        // A busca será refeita no próximo click do botão "Filtrar"
    };

    // --- Funções de Ação (Visualizar, Gerar Boleto, Receber, Deletar ) ---

    const handleView = (divida) => { setModalMode('view'); setSelectedDivida(divida); setShowModal(true); };

    const handleEdit = (divida) => { setModalMode('edit'); setSelectedDivida(divida); setShowModal(true); };


    const handleDelete = async (id) => {
        if (!window.confirm(`Confirma a exclusão da dívida ID ${id}?`)) return;
        const toastId = toast.loading('Excluindo...');
        try {
            await axios.delete(`${URL_BACKEND}/divida/${id}`, { headers: authHeader });
            toast.success('Dívida excluída!', { id: toastId });
            fetchDividas();
        } catch (error) {
            toast.error('Erro ao excluir dívida.', { id: toastId });
        }
    };

    const handleGerarBoleto = async (id) => {
        if (!window.confirm(`Confirma a geração de boleto para essa dívida ID ${id}?`)) return;
        const toastId = toast.loading('Gerando boleto...');
        // try {
        //     await axios.delete(`${URL_BACKEND}/divida/${id}`, { headers: authHeader });
        //     toast.success('Dívida excluída!', { id: toastId });
        //     fetchDividas();
        // } catch (error) {
        //     toast.error('Erro ao excluir dívida.', { id: toastId });
        // }
    };

    const handleBaixarDivida = async (id) => {
        if (!window.confirm(`Confirma a baixa dessa dívida ID ${id}?`)) return;
        const toastId = toast.loading('Baixando...');
        // try {
        //     await axios.delete(`${URL_BACKEND}/divida/${id}`, { headers: authHeader });
        //     toast.success('Dívida excluída!', { id: toastId });
        //     fetchDividas();
        // } catch (error) {
        //     toast.error('Erro ao excluir dívida.', { id: toastId });
        // }
    };


    return (
        <div style={ui.container}>

            {/* Cabeçalho */}
            <div style={{ ...ui.header, background: 'linear-gradient(to right, #6d28d9, #4f46e5)', padding: 10 }}>
                <h1 style={{ fontSize: '1.4rem', fontWeight: '600', color: '#fff' }}>
                    Visão Global de Todas as Dívidas
                </h1>
                <button
                    onClick={() => navigate('/devedores')}
                    style={{ ...ui.homeButton, background: 'transparent', border: '1px solid rgba(255,255,255,0.5)' }}
                >
                    <FaArrowLeft /> Voltar
                </button>
            </div>

            {/* --- CARD DE DESTAQUE: SALDO TOTAL --- */}
            <div style={{
                ...ui.tableCard, // Reutilizando um estilo base para o card
                padding: '10px 20px',
                margin: '15px 0',
                backgroundColor: totalDividasAbertas > 0 ? '#fee2e2' : '#d1fae5', // Fundo vermelho claro se houver saldo
                borderLeft: `6px solid ${totalDividasAbertas > 0 ? '#ef4444' : '#059669'}`, // Borda lateral colorida
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <h2 style={{
                    fontSize: '1.4rem',
                    fontWeight: '600',
                    color: '#1f2937'
                }}>
                    Saldo **TOTAL** das Dívidas
                </h2>
                <span style={{
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    color: totalDividasAbertas > 0 ? '#ef4444' : '#059669', // Cor do texto (Vermelho ou Verde)
                    marginLeft: '20px',
                }}>
                    {formatCurrency(totalDividasAbertas)}
                </span>
            </div>
            {/* --- FIM DO CARD DE DESTAQUE --- */}

            {/* Ações e Filtros */}
            <div style={{ ...ui.filters, justifyContent: 'space-between', flexWrap: 'wrap' }}>

                {/* --- SEÇÃO DE FILTROS GERAIS --- */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', paddingBottom: 8, flexWrap: 'wrap' }}>

                    {/* NOVO: Filtro por Cliente Contratante */}
                    <div style={{ ...ui.formGroup, gridColumn: 'span 3' }}> {/* Ocupa 3 colunas */}
                        <label style={ui.label} htmlFor="clienteContratante">Cliente (Contratante) *</label>
                        <select
                            id="clienteContratante"
                            style={ui.select}
                            value={filterClienteContratanteId}
                            onChange={e => setFilterClienteContratanteId(e.target.value)}
                            disabled={loading}
                            required
                        >
                            <option value="">Selecione o Cliente Contratante...</option>
                            {clientesContratantes.map(c => (
                                <option key={c.id} value={String(c.id)}>{c.nome}</option>
                            ))}
                        </select>
                    </div>

                    {/* NOVO: Filtro por ID do Devedor */}
                    <div style={{ ...ui.formGroup, gridColumn: 'span 3' }}> {/* Ocupa 3 colunas */}
                        <label style={ui.label} htmlFor="devedor">Devedor</label>
                        <select
                            id="devedorId"
                            style={ui.select}
                            value={filterDevedorId}
                            onChange={e => setFilterDevedorId(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Selecione um devedor...</option>
                            {allDevedores.map(c => (
                                <option key={c.id} value={String(c.id)}>{c.nome}</option>
                            ))}
                        </select>
                    </div>




                    {/* Filtro de Status */}
                    <div style={ui.filterGroup}>
                        <label style={ui.label} htmlFor="status">Status</label>
                        <select
                            id="status"
                            style={ui.select}
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                            disabled={loading}
                        >
                            <option value="Todas">Todas</option>
                            <option value="Aberto">Aberto</option>
                            <option value="Pago">Pago</option>
                        </select>
                    </div>

                    {/* Filtros de Data */}
                    <div style={ui.filterGroup}>
                        <label style={ui.label} htmlFor="dataInicio">Vencimento De:</label>
                        <input
                            id="dataInicio"
                            type="date"
                            style={ui.input}
                            value={filterDataInicio}
                            onChange={e => setFilterDataInicio(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div style={ui.filterGroup}>
                        <label style={ui.label} htmlFor="dataFim">Vencimento Até:</label>
                        <input
                            id="dataFim"
                            type="date"
                            style={ui.input}
                            value={filterDataFim}
                            onChange={e => setFilterDataFim(e.target.value)}
                            disabled={loading}
                        />
                    </div>


                    {/* Botão de Aplicar Filtros */}
                    <button
                        style={{ ...ui.buttonPrimary, background: '#a855f7', height: ui.input.height, display: 'flex', alignItems: 'center', gap: 5 }}
                        onClick={fetchDividas}
                        disabled={loading}
                        title="Aplicar Filtros"
                    >
                        <FaFilter /> Filtrar
                    </button>

                    {/* Botão de Limpar Filtros */}
                    <button
                        style={{ ...ui.buttonPrimary, background: '#ef4444', height: ui.input.height, display: 'flex', alignItems: 'center', gap: 5 }}
                        onClick={handleClearFilters}
                        disabled={loading}
                        title="Limpar Filtros"
                    >
                        <FaTimes /> Limpar
                    </button>

                </div>
            </div>

            {/* TABELA DE DÍVIDAS (Copiada do componente anterior) */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p style={{ padding: 16 }}>Carregando Dívidas...</p>
                ) : allDividas.length === 0 ? (
                    <p style={{ color: 'red', padding: 16 }}>Nenhuma dívida encontrada com os filtros aplicados.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    {/* <th style={{ ...ui.th, width: '120px' }}>ID Dívida</th> */}
                                    {/* NOVAS COLUNAS */}
                                    <th style={{ ...ui.th, width: '150px' }}>Cliente Contratante</th>
                                    <th style={{ ...ui.th, width: '150px' }}>Devedor</th>
                                    {/* FIM NOVAS COLUNAS */}
                                    <th style={{ ...ui.th, width: '60px' }}>Status</th>
                                    <th style={{ ...ui.th, width: '80px' }}>Valor Original</th>
                                    <th style={{ ...ui.th, width: '60px' }}>Vencimento</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allDividas.map((d, idx) => (
                                    <tr key={d.id} style={{ ...ui.tr, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                        {/* <td style={ui.tdMono}>#{d.id}</td> */}
                                        {/* NOVOS DADOS */}
                                        <td style={ui.tdClamp}>{d.nomeCliente || `ID: ${d.id_cliente}`}</td>
                                        <td style={ui.tdClamp}>{d.nomeDevedor || `ID: ${d.id_devedor}`}</td>
                                        {/* FIM NOVOS DADOS */}
                                        <td style={ui.tdClamp}>{d.status}</td>
                                        <td style={ui.tdMono}>{formatCurrency(convertBRLToNumber(d.valor_original))}</td>
                                        <td style={ui.tdClamp}>
                                            {d.vencimento ? new Date(d.vencimento).toLocaleDateString('pt-BR') : 'N/A'}
                                        </td>
                                        <td style={ui.tdActions}>
                                            <button title="Visualizar" style={ui.actionView} onClick={() => handleView(d)}><FaEye /></button>
                                            {/*<button title="Editar" style={ui.actionEdit} onClick={() => handleEdit(d)}><FaEdit /></button>
                                            <button title="Excluir" style={ui.actionDelete} onClick={() => handleDelete(d.id)}><FaTrash /></button>
                                             <button
                                                title="Gerar Boleto"
                                                style={{ ...ui.actionEdit, backgroundColor: '#0ea5e9', color: '#000' }} // Cor azul
                                                onClick={() => handleGerarBoleto(d.id)}
                                            >
                                                <FaBarcode />
                                            </button> 
                                    
                                            <button
                                                title="Receber Divida"
                                                style={{ ...ui.actionEdit, backgroundColor: '#10b981' }} // Cor verde
                                                onClick={() => handleBaixarDivida(d.id)}
                                            >
                                                <FaMoneyBillWave />
                                            </button>*/}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ... Modal (Opcional, se precisar de CRUD nesta tela) ... */}
            <DividaModal
                show={showModal}
                onClose={() => setShowModal(false)}
                divida={selectedDivida}
                onSave={fetchDividas}
                mode={modalMode}

            />

        </div>
    );
};

export default GlobalDividasScreen;